import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChartLine, Envelope, Lock, User, GoogleLogo, ArrowRight } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const Register = () => {
  const navigate = useNavigate();
  const { register, googleLogin } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1200)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-outfit text-3xl font-light text-white mb-4">
            Join the Elite <span className="text-[#D4AF37]">Trading Community</span>
          </h2>
          <p className="text-zinc-400">
            Get access to professional trading signals, expert courses, and exclusive merchandise.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="register-logo">
            <ChartLine className="w-10 h-10 text-[#D4AF37]" weight="duotone" />
            <span className="font-outfit text-2xl font-semibold text-white">
              THE VAULT<span className="text-[#D4AF37]"> by 1000Banks</span>
            </span>
          </Link>

          <h1 className="font-outfit text-3xl font-light text-white mb-2" data-testid="register-title">
            Create Account
          </h1>
          <p className="text-zinc-400 mb-8">
            Start your trading journey today
          </p>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] mb-6 py-6"
            onClick={googleLogin}
            data-testid="google-register-btn"
          >
            <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#050505] text-zinc-500">or register with email</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 mb-6 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-zinc-400 text-sm">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  placeholder="John Doe"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-zinc-400 text-sm">Email</Label>
              <div className="relative mt-1">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  placeholder="you@example.com"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-400 text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>
              <p className="text-xs text-zinc-600 mt-1">Minimum 6 characters</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C158] py-6"
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-zinc-500 text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
